import { useEffect, useState, useMemo } from 'react';

import { CalendarIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router';
import { useTitle } from 'react-use';

import { SettingsContent } from '@/components/templates/SettingsContent/SettingsContent';
import { EmptySkeleton } from '@/components/ui/custom/EmptySkeleton';
import { SimpleFilterCombobox } from '@/components/ui/custom/SimpleFilterCombobox';
import { DataTable } from '@/components/ui/table/data-table';
import { useEmptyStateView } from '@/hooks/useEmptyStateView';
import { usePermissions } from '@/hooks/usePermissions';
import { useBilling } from '@/modules/billing/hooks/useBilling';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug } from '@/redux/session/selectors';
import useSegments from '@/segment/useSegments';
import { actions } from '@/shared/types/permissions';
import { PageName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';

import { columns } from './columns';
import { EmptyInvoices } from './EmptyInvoices';

/**
 * Invoices page component that displays payment history and invoice management.
 *
 * Features:
 * - Displays past invoices in a table
 * - Provides year-based filtering
 * - Handles permission-based access control
 * - Shows contact information for billing inquiries
 */
const Invoices = () => {
  // Hooks
  const { t } = useTranslation();
  const { listStatus } = usePermissions();
  const { account, pastInvoices, isPastInvoicesLoading, billingState } = useBilling();

  // Selectors
  const slug = useSelector(selectAccountSlug);
  const canReadPage = useSelector((state: RootState) =>
    getPermissions(state, 'billing', actions.READ)
  );

  // Derived values
  const isPlanEnterprise = billingState?.plan_id === 'enterprise';

  // Page title and analytics
  useTitle(t('pages.invoices'));
  useSegments(PageName.INVOICES);

  // Generate year options for filter dropdown from invoice data
  const yearOptions = useMemo(() => {
    if (!pastInvoices) {
      return [];
    }

    const years = Array.from(
      new Set(
        pastInvoices.map((invoice) =>
          new Date(invoice.period_start * 1000).getFullYear().toString()
        )
      )
    ).sort((a, b) => Number(b) - Number(a));

    return years.map((year) => ({ label: year, value: year }));
  }, [pastInvoices]);

  // Auto-select first year when data loads
  const [selectedYear, setSelectedYear] = useState('');
  useEffect(() => {
    if (yearOptions.length > 0 && !selectedYear) {
      setSelectedYear(yearOptions[0].value);
    }
  }, [yearOptions, selectedYear]);

  // Filter invoices by selected year
  const filteredInvoices = useMemo(() => {
    if (!pastInvoices || !selectedYear) {
      return [];
    }

    return pastInvoices
      .filter((inv) => {
        const start = new Date(inv.period_start * 1000);
        return start.getFullYear().toString() === selectedYear;
      })
      .map((inv) => {
        const start = new Date(inv.period_start * 1000);
        const dateTo = new Date(inv.period_end * 1000);

        return { ...inv, calcValues: { dateFrom: start, dateTo, year: `${start.getFullYear()}` } };
      });
  }, [pastInvoices, selectedYear]);

  // Handle year filter selection
  const handleYearFilterChange = (year: string) => {
    setSelectedYear(year);
  };

  const { showEmpty, showEmptySkeleton, showList, isEmptyPage } = useEmptyStateView({
    scope: 'invoices',
    isLoading: isPastInvoicesLoading,
    isEmpty: !isPlanEnterprise && pastInvoices?.length === 0,
    isReady: account !== undefined,
  });

  // Permission check - redirect if no access
  if (listStatus === 'success' && !canReadPage) {
    return <Navigate to={`/${slug}/account/information`} replace />;
  }

  return (
    <SettingsContent fill>
      {!isEmptyPage && (
        <div className="mb-4 flex items-center justify-between self-stretch">
          <h1 className="text-lg leading-none font-semibold tracking-[-0.45px] text-foreground">
            {t('pages.invoices')}
          </h1>

          {!isPlanEnterprise && yearOptions.length > 0 && (
            <SimpleFilterCombobox
              options={yearOptions}
              value={selectedYear}
              onChange={handleYearFilterChange}
              icon={<CalendarIcon />}
            />
          )}
        </div>
      )}

      {!isPlanEnterprise && (
        <>
          {showEmpty && <EmptyInvoices />}

          {showEmptySkeleton && <EmptySkeleton media="icon" descriptionLines={2} actions={0} />}

          {showList && (
            <DataTable.Root
              fill
              className="min-h-0 flex-1"
              columns={columns()}
              data={filteredInvoices}
              rowCount={filteredInvoices.length}
              resourceName="invoice"
              isLoading={isPastInvoicesLoading}
              emptyStateMessage={t('invoices.no_invoices')}
            >
              <DataTable.Content />
            </DataTable.Root>
          )}
        </>
      )}

      {!isEmptyPage && (
        <div className="my-4 text-xs leading-4 font-normal text-foreground-muted">
          <span className="text-sm leading-5 font-normal text-foreground-muted">
            {isPlanEnterprise
              ? t('invoices.contact', { 0: billingState?.plan_name })
              : t('invoices.missing_invoice_disclaimer')}{' '}
            <a
              href={`mailto:${t('contact.billing_email')}`}
              className="font-medium text-primary hover:underline"
            >
              {t('contact.billing_email')}
            </a>
          </span>
        </div>
      )}
    </SettingsContent>
  );
};

export default Invoices;
